import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getPoojariById } from '../../services/poojariService';
import { getBookingQuote, isRangeAvailable } from '../../services/bookingService';
import {
    createBooking,
    getBookedSlots
} from '../../services/bookingService';
import {
    createRazorpayOrder,
    verifyRazorpayPayment
} from '../../services/paymentService';
import { formatPaise } from '../../utils/money';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatDuration } from '../../utils/poojaTypes';
import {
    generateSlots,
    groupSlots,
    getNextDays,
    formatDateShort,
    formatTime12h
} from '../../utils/slotUtils';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const ADVANCE_PERCENTAGE = 25;

const CreateBooking = () => {
    const { poojariId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, user } = useAuth();

    const [poojari, setPoojari] = useState(null);
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);

    // Payment modal - booking banne ke baad khulega
    const [createdBooking, setCreatedBooking] = useState(null);
    const [payMethod, setPayMethod] = useState('UPI');
    const [paying, setPaying] = useState(false);

    // Step 1
    const [service, setService] = useState(null);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [bookedSlots, setBookedSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [showAllDates, setShowAllDates] = useState(false);

    // Step 2
    const [address, setAddress] = useState({
        poojaAddress: '',
        poojaCity: '',
        poojaState: '',
        poojaPincode: '',
        specialInstructions: ''
    });

    // Step 3
    const [wantSamagri, setWantSamagri] = useState(false);
    const [quote, setQuote] = useState(null);
    const [rangeFree, setRangeFree] = useState(null);
    const [samagriQuote, setSamagriQuote] = useState(null);
    const [paymentMode, setPaymentMode] =
        useState('PARTIAL_ADVANCE');

    const days = getNextDays(14);

    useEffect(() => {
        if (!token) {
            toast.warning('Please login to book');
            navigate('/login');
            return;
        }
        fetchPoojari();
    }, [poojariId]);

    // ⚠️ Amount BACKEND se laao — apna jod mat lagao.
    //    Samagri ka 3% charge sirf backend jaanta hai.
    useEffect(() => {
        if (!service?.poojaType || !poojariId) {
            setQuote(null);
            setSamagriQuote(null);
            return;
        }

        let alive = true;

        getBookingQuote(poojariId, service.poojaType, wantSamagri)
            .then(data => { if (alive) setQuote(data); })
            .catch(() => {
                // ⚠️ Fail ho to quote null — upar wala fallback
                //    purana jod dikha dega. Page atakna nahi chahiye.
                if (alive) setQuote(null);
            });

        // ⚠️ Checkbox ka daam — hamesha samagri ke saath. Isse tick
        //    karne se pehle aur baad mein ek hi number dikhta hai.
        //
        // ⚠️ Ye call `return` se PEHLE honi chahiye. Pehle ye cleanup
        //    ke baad likhi thi, isliye kabhi chalti hi nahi thi aur
        //    checkbox pe purana ₹200 dikhta rehta tha.
        if (service?.samagriAvailable) {
            getBookingQuote(poojariId, service.poojaType, true)
                .then(data => { if (alive) setSamagriQuote(data); })
                .catch(() => { if (alive) setSamagriQuote(null); });
        }

        if (service?.samagriAvailable) {
            getBookingQuote(poojariId, service.poojaType, true)
                .then(data => { if (alive) setSamagriQuote(data); })
                .catch(() => { if (alive) setSamagriQuote(null); });
        }

        return () => { alive = false; };
    }, [poojariId, service?.poojaType, wantSamagri]);

    // User ka address pehle se bhar do
    useEffect(() => {
        if (user?.address) {
            setAddress(a => ({
                ...a,
                poojaAddress: user.address
            }));
        }
    }, [user]);

    useEffect(() => {
        if (!date) {
            setBookedSlots([]);
            return;
        }
        fetchSlots(date);
        setTime('');
    }, [date]);

    
    // ⚠️ Multi-day pooja mein date chunte hi POORA range check karo.
    //    Single-day mein slots se hi pata chal jaata hai.
    //
    // ⚠️ Bina iske user 3 step bhar ke aakhir mein "already booked
    //    on 25 Sep" dekhta hai - aur tab tak wo poora form bhar
    //    chuka hota hai.
    useEffect(() => {
        const days = service?.durationDays || 1;

        if (days <= 1 || !date || !poojariId) {
            setRangeFree(null);
            return;
        }

        let alive = true;

        isRangeAvailable(poojariId, date, days)
            .then(ok => { if (alive) setRangeFree(ok); })
            .catch(() => {
                // ⚠️ Fail ho to null - user ko rokte nahi. Asli rok
                //    backend mein hai (createBooking), wo phir bhi
                //    lagegi.
                if (alive) setRangeFree(null);
            });

        return () => { alive = false; };
    }, [poojariId, date, service?.durationDays]);

    const fetchPoojari = async () => {
        try {
            const data = await getPoojariById(poojariId);
            setPoojari(data);
            // ⚠️ URL se aayi pooja pehle se select kar do.
            //
            // Naam se match kar rahe hain, index se nahi - poojari
            // apni services ka order kabhi bhi badal sakta hai,
            // aur tab index galat pooja chun leta.
            const preSelected = searchParams.get('pooja');
            if (preSelected && data.services?.length) {
                const match = data.services.find(
                    s => s.poojaType === preSelected);
                if (match) {
                    setService(match);
                }
            }
            if (data.city) {
                setAddress(a => ({
                    ...a,
                    poojaCity: a.poojaCity || data.city,
                    poojaState: a.poojaState || data.state
                }));
            }
        } catch (error) {
            toast.error('Could not load poojari');
        }
    };

    const fetchSlots = async (d) => {
        setLoadingSlots(true);
        try {
            const data = await getBookedSlots(poojariId, d);
            setBookedSlots(Array.isArray(data) ? data : []);
        } catch (error) {
            setBookedSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    // ===== Amounts - SAB PAISE MEIN =====
    //
    // ⚠️ Backend ab priceInPaise bhejta hai (50000 = ₹500).
    // Yahan sab paise mein rakhte hain aur dikhate waqt
    // formatPaise() lagate hain. Beech mein rupees mein convert
    // karne se float ki galtiyan aati hain.

    const baseFee = service?.priceInPaise || 0;
    const samagriFee = wantSamagri && service?.samagriAvailable
        ? (service.samagriPriceInPaise || 0) : 0;
    const total = quote?.totalAmount != null
        ? quote.totalAmount : baseFee + samagriFee;
    const samagriDisplay = quote?.totalAmount != null
        ? total - baseFee : samagriFee;

    // ⚠️ Integer math - pehle multiply, phir divide.
    // "+ 50" round-half-up ke liye (100 ka aadha).
    // Backend mein bilkul yahi formula hai, isliye dono taraf
    // ek hi number banta hai. Purana Math.round(x/100*100)/100
    // rupees ke liye tha - paise mein wo galat hota.

    const payNow = paymentMode === 'FULL_ADVANCE'
        ? total
        : paymentMode === 'PARTIAL_ADVANCE'
            ? Math.floor((total * ADVANCE_PERCENTAGE + 50) / 100)
            : 0;

    const payLater = total - payNow;

    // ===== Navigation =====

    const canGoStep2 = service && date && time && rangeFree !== false;
    const canGoStep3 = address.poojaAddress.trim()
        && address.poojaCity.trim()
        && address.poojaState.trim();

    const goNext = () => {
        if (step === 1) {
            if (!service) return toast.warning('Select a pooja');
            if (!date) return toast.warning('Select a date');
            if (!time) return toast.warning('Select a time');
            setStep(2);
        } else if (step === 2) {
            if (!canGoStep3) {
                return toast.warning('Fill the address');
            }
            if (address.poojaPincode
                && !/^\d{6}$/.test(address.poojaPincode)) {
                return toast.warning('Pincode must be 6 digits');
            }
            setStep(3);
        }
        window.scrollTo(0, 0);
    };

    const goBack = () => {
        if (step === 1) return navigate(-1);
        setStep(step - 1);
        window.scrollTo(0, 0);
    };

    const submit = async () => {
        const userId = user?.id;
        if (!userId) {
            toast.error('Session expired. Login again.');
            return navigate('/login');
        }
        setSaving(true);
        try {
            const booking = await createBooking({
                userId,
                poojariId: parseInt(poojariId),
                poojaType: service.poojaType,
                poojaDate: date,
                poojaTime: time,
                paymentMode,
                samagriIncluded: wantSamagri,
                ...address
            });

            // Pay after pooja - abhi kuch nahi dena
            if (paymentMode === 'PAY_AFTER_POOJA') {
                toast.success('Booking created');
                return navigate('/home',
                    { state: { tab: 'active' } });
            }

            // Advance dena hai - payment modal kholo
            setCreatedBooking(booking);

        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Booking failed');
        } finally {
            setSaving(false);
        }
    };

    /**
      * Razorpay Checkout kholo.
      *
      * ⚠️ Purana makePayment() HATA diya gaya. Wo bina kisi asli
      * payment ke booking ko "paid" mark kar deta tha - koi bhi
      * browser console se wo call karke free booking le sakta tha.
      *
      * Naya flow:
      *   1. Backend pe order banao (amount backend hi nikalta hai)
      *   2. Razorpay Checkout kholo
      *   3. User pay kare -> handler chalta hai
      *   4. Backend signature verify kare -> TABHI payment maana jaata hai
      *
      * ⚠️ Step 4 ke bina koi bhi "payment ho gaya" POST kar sakta hai.
      */
    const confirmPayment = async () => {
        if (!createdBooking) return;

        // Razorpay ka script load hua ya nahi
        if (!window.Razorpay) {
            toast.error('Payment system not ready. '
                + 'Please refresh and try again.');
            return;
        }

        setPaying(true);
        try {
            // ⚠️ Amount NAHI bhejte - backend booking se nikalta hai.
            // Frontend se amount lene ka matlab hota ki koi bhi
            // console se Rs 1 bhej ke Rs 5000 ki pooja book kar leta.
            const order = await createRazorpayOrder(
                createdBooking.id,
                paymentMode === 'FULL_ADVANCE' ? 'FULL' : 'ADVANCE'
            );

            const options = {
                key: order.razorpayKeyId,
                order_id: order.razorpayOrderId,
                amount: order.amountInPaise,
                currency: order.currency || 'INR',
                name: 'Yajman',
                description: order.poojaType || 'Pooja booking',

                prefill: {
                    name: order.userName || '',
                    email: order.userEmail || '',
                    contact: order.userPhone || ''
                },

                theme: { color: RED },

                // Payment success - ⚠️ ye abhi confirm NAHI hai.
                // Backend signature verify karega tabhi maana jayega.
                handler: async (response) => {
                    try {
                        await verifyRazorpayPayment(response);
                        toast.success('Payment successful');
                        navigate('/home', { state: { tab: 'active' } });
                    } catch (err) {
                        toast.error(err.response?.data?.message
                            || 'Payment verification failed. If money '
                            + 'was deducted it will be refunded.');
                    } finally {
                        setPaying(false);
                    }
                },

                modal: {
                    // User ne Checkout band kar diya bina pay kiye.
                    // ⚠️ Booking rehne dete hain - wo already ban chuki
                    // hai. User baad mein "Pay now" se pay kar sakta hai.
                    ondismiss: () => {
                        setPaying(false);
                        toast.info('Payment cancelled. You can pay '
                            + 'later from My Bookings.');
                        navigate('/home', { state: { tab: 'active' } });
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            // Payment fail hua (galat OTP, balance kam, waghera)
            rzp.on('payment.failed', (response) => {
                setPaying(false);
                toast.error(response.error?.description
                    || 'Payment failed. Please try again.');
            });

            rzp.open();

        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not start payment');
            setPaying(false);
        }
    };

    // Payment skip - booking rahegi, baad mein pay karega
    const skipPayment = () => {
        toast.info('Booking created. You can pay from '
            + 'My Bookings anytime.');
        navigate('/home', { state: { tab: 'active' } });
    };

    if (!poojari) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    const services = poojari.services || [];
    const slots = service
        ? generateSlots(service.durationMinutes, bookedSlots, date)
        : [];
    const slotGroups = groupSlots(slots);

    const steps = [
        { n: 1, label: 'When' },
        { n: 2, label: 'Where' },
        { n: 3, label: 'Pay' }
    ];

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen pb-has-action-bar">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <div className="d-flex align-items-center gap-2">
                    <button
                        onClick={goBack}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: GOLD,
                            fontSize: '22px',
                            lineHeight: 1,
                            padding: 0
                        }}>
                        ←
                    </button>
                    <div>
                        <p className="mb-0 fw-bold"
                            style={{
                                color: GOLD,
                                fontSize: '15px'
                            }}>
                            Book {poojari.firstName} {poojari.lastName}
                        </p>
                        <p className="mb-0"
                            style={{
                                color: '#FFE4B5',
                                fontSize: '12px'
                            }}>
                            📍 {poojari.city} · ⭐ {poojari.rating > 0
                                ? poojari.rating : 'New'}
                        </p>
                    </div>
                </div>
            </div>

            {/* ===== STEPS ===== */}
            <div className="pb-steps">
                {steps.map(s => (
                    <div key={s.n}
                        className={`pb-step ${step === s.n
                            ? 'active' : step > s.n ? 'done' : ''}`}>
                        <div className="pb-step-bar"></div>
                        <p className="pb-step-label">
                            {s.n} · {s.label}
                        </p>
                    </div>
                ))}
            </div>

            <div className="container" style={{ maxWidth: '640px' }}>
                <div className="py-3">

                    {/* ===================================== */}
                    {/* STEP 1 - WHEN                         */}
                    {/* ===================================== */}
                    {step === 1 && (
                        <>
                            <p className="fw-bold mb-2"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                Which pooja
                            </p>

                            {services.length === 0 ? (
                                <p className="text-muted"
                                    style={{ fontSize: '14px' }}>
                                    This poojari has not added any
                                    services yet.
                                </p>
                            ) : services.map((s, i) => (
                                <div key={i}
                                    className={`pb-option ${service?.poojaType === s.poojaType
                                        ? 'selected' : ''}`}
                                    onClick={() => {
                                        setService(s);
                                        setTime('');
                                        setWantSamagri(false);
                                    }}>
                                    <div className="pb-radio"></div>
                                    <div className="flex-grow-1">
                                        <p className="mb-0"
                                            style={{
                                                fontSize: '14px',
                                                color: DARK_RED,
                                                fontWeight: 600
                                            }}>
                                            {s.poojaType}
                                        </p>
                                        <p className="mb-0 text-muted"
                                            style={{
                                                fontSize: '12px'
                                            }}>
                                            {formatDuration(
                                                s.durationMinutes, s.durationDays)}
                                            {s.samagriAvailable
                                                ? ' · samagri available'
                                                : ' · you arrange samagri'}
                                        </p>
                                    </div>
                                    <strong style={{
                                        fontSize: '15px',
                                        color: RED
                                    }}>
                                        {formatPaise(s.priceInPaise)}
                                    </strong>
                                </div>
                            ))}

                            {service && (
                                <>
                                    <div className="d-flex justify-content-between align-items-baseline mt-4 mb-2">
                                        <p className="fw-bold mb-0"
                                            style={{
                                                color: DARK_RED,
                                                fontSize: '14px'
                                            }}>
                                            Pick a date
                                        </p>
                                        <button
                                            className="btn btn-sm p-0"
                                            style={{
                                                color: RED,
                                                fontSize: '13px',
                                                border: 'none',
                                                background: 'none',
                                                minHeight: 'auto'
                                            }}
                                            onClick={() =>
                                                setShowAllDates(
                                                    !showAllDates)}>
                                            {showAllDates
                                                ? 'Quick pick'
                                                : 'Other date'}
                                        </button>
                                    </div>

                                    {showAllDates ? (
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={date}
                                            min={new Date()
                                                .toISOString()
                                                .split('T')[0]}
                                            onChange={e =>
                                                setDate(e.target.value)}
                                            style={{
                                                borderColor: GOLD
                                            }}
                                        />
                                    ) : (
                                        <div className="pb-date-strip">
                                            {days.map(d => (
                                                <div key={d.value}
                                                    className={`pb-date-chip ${date === d.value
                                                        ? 'selected' : ''}`}
                                                    onClick={() =>
                                                        setDate(d.value)}>
                                                    <p className="pb-day">
                                                        {d.isToday
                                                            ? 'TODAY'
                                                            : d.dayName}
                                                    </p>
                                                    <p className="pb-num">
                                                        {d.dayNum}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {service && date && (
                                <>
                                    <p className="fw-bold mt-4 mb-2"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '14px'
                                        }}>
                                        Start time
                                    </p>

                                    {loadingSlots ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm"
                                                style={{ color: RED }}>
                                            </div>
                                        </div>
                                    ) : slots.length === 0 ? (
                                        <p className="text-muted"
                                            style={{ fontSize: '13px' }}>
                                            No slots available on this
                                            date. Try another day.
                                        </p>
                                    ) : (
                                        slotGroups.map(g => (
                                            <div key={g.key}
                                                className="mb-3">
                                                <p className="pb-slot-label">
                                                    {g.label.toUpperCase()}
                                                </p>
                                                <div className="pb-slot-grid">
                                                    {g.items.map(s => (
                                                        <div
                                                            key={s.value}
                                                            className={`pb-slot ${!s.available
                                                                ? 'disabled'
                                                                : time === s.value
                                                                    ? 'selected' : ''}`}
                                                            onClick={() =>
                                                                s.available
                                                                && setTime(s.value)}>
                                                            {s.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}

                                                                        {/* ⚠️ Multi-day pooja mein range busy ho to
                                        YAHIN bata do. Pehle user 3 step bhar ke
                                        aakhir mein "already booked on 25 Sep"
                                        dekhta tha - tab tak wo poora form bhar
                                        chuka hota tha. */}
                                    {time && rangeFree === false && (
                                        <div className="p-3 mt-2"
                                            style={{
                                                backgroundColor: '#FFEBEE',
                                                borderRadius: '10px'
                                            }}>
                                            <p className="mb-0"
                                                style={{
                                                    color: '#C62828',
                                                    fontSize: '13px'
                                                }}>
                                                Poojari is not free for all{' '}
                                                {service?.durationDays} days from this
                                                date. Please choose another date.
                                            </p>
                                        </div>
                                    )}

                                    {time && rangeFree !== false && (
                                        <div className="p-3 mt-2"
                                            style={{
                                                backgroundColor: '#EAF3DE',
                                                borderRadius: '10px'
                                            }}>
                                            <p className="mb-0"
                                                style={{
                                                    color: '#27500A',
                                                    fontSize: '13px'
                                                }}>
                                                ✓ {formatDateShort(date)},{' '}
                                                {formatTime12h(time)}
                                                {(service?.durationDays || 1) > 1 ? (
                                                    <>
                                                        {' → '}
                                                        {formatDateShort(
                                                            new Date(new Date(date).getTime()
                                                                + (service.durationDays - 1)
                                                                * 86400000)
                                                                .toISOString().split('T')[0])}
                                                        {` (${service.durationDays} days)`}
                                                    </>
                                                ) : (
                                                    <>
                                                        {' to '}
                                                        {slots.find(s => s.value === time)
                                                            ?.endLabel}
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* ===================================== */}
                    {/* STEP 2 - WHERE                        */}
                    {/* ===================================== */}
                    {step === 2 && (
                        <>
                            <p className="fw-bold mb-1"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                Where is the pooja
                            </p>
                            <p className="text-muted mb-3"
                                style={{ fontSize: '13px' }}>
                                The poojari will come to this address
                            </p>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Full address
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    placeholder="House no, street, landmark"
                                    value={address.poojaAddress}
                                    onChange={e =>
                                        setAddress({
                                            ...address,
                                            poojaAddress: e.target.value
                                        })}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                                {user?.address && (
                                    <small className="text-muted">
                                        Filled from your profile —
                                        change if needed
                                    </small>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    City
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="City"
                                    value={address.poojaCity}
                                    onChange={e =>
                                        setAddress({
                                            ...address,
                                            poojaCity: e.target.value
                                        })}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-7">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="State"
                                        value={address.poojaState}
                                        onChange={e =>
                                            setAddress({
                                                ...address,
                                                poojaState: e.target.value
                                            })}
                                        style={{ borderColor: GOLD }}
                                    />
                                </div>
                                <div className="col-5">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Pincode
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="751001"
                                        maxLength="6"
                                        inputMode="numeric"
                                        value={address.poojaPincode}
                                        onChange={e =>
                                            setAddress({
                                                ...address,
                                                poojaPincode:
                                                    e.target.value
                                                        .replace(
                                                            /\D/g, '')
                                            })}
                                        style={{ borderColor: GOLD }}
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Anything the poojari should know
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Number of people, special requests, parking..."
                                    value={address.specialInstructions}
                                    onChange={e =>
                                        setAddress({
                                            ...address,
                                            specialInstructions:
                                                e.target.value
                                        })}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                            </div>
                        </>
                    )}

                    {/* ===================================== */}
                    {/* STEP 3 - PAY                          */}
                    {/* ===================================== */}
                    {step === 3 && (
                        <>
                            {/* Summary */}
                            <div className="card mb-3"
                                style={{
                                    borderRadius: '12px',
                                    border: `1px solid ${GOLD}`
                                }}>
                                <div className="card-body p-3">
                                    <p className="fw-bold mb-2"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '14px'
                                        }}>
                                        {service.poojaType}
                                    </p>
                                    <p className="text-muted mb-1"
                                        style={{ fontSize: '13px' }}>
                                        📅 {formatDateShort(date)},{' '}
                                        {formatTime12h(time)}
                                    </p>
                                    <p className="text-muted mb-0"
                                        style={{ fontSize: '13px' }}>
                                        📍 {address.poojaAddress},{' '}
                                        {address.poojaCity}
                                    </p>
                                </div>
                            </div>

                            {/* Samagri */}
                            {service.samagriAvailable && (
                                <div
                                    className={`pb-option ${wantSamagri
                                        ? 'selected' : ''}`}
                                    onClick={() =>
                                        setWantSamagri(!wantSamagri)}>
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px',
                                        border: `2px solid ${wantSamagri
                                            ? RED : '#ccc'}`,
                                        backgroundColor: wantSamagri
                                            ? RED : 'white',
                                        color: GOLD,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '13px',
                                        flexShrink: 0
                                    }}>
                                        {wantSamagri && '✓'}
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="mb-0"
                                            style={{
                                                fontSize: '14px',
                                                color: DARK_RED,
                                                fontWeight: 600
                                            }}>
                                            Poojari brings samagri
                                        </p>
                                        <p className="mb-0 text-muted"
                                            style={{ fontSize: '12px' }}>
                                            You don't need to arrange
                                            anything
                                        </p>
                                    </div>
                                    <strong style={{
                                        fontSize: '14px',
                                        color: RED
                                    }}>
                                        +{formatPaise(samagriQuote?.totalAmount != null ? samagriQuote.totalAmount - baseFee : service.samagriPriceInPaise)}
                                    </strong>
                                </div>
                            )}

                            {/* Bill */}
                            <div className="p-3 my-3"
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    border: '1px solid #eee'
                                }}>
                                <div className="d-flex justify-content-between mb-2"
                                    style={{ fontSize: '14px' }}>
                                    <span className="text-muted">
                                        Poojari fees
                                    </span>
                                    <span>{formatPaise(baseFee)}</span>
                                </div>
                                {samagriDisplay > 0 && (
                                    <div className="d-flex justify-content-between mb-2"
                                        style={{ fontSize: '14px' }}>
                                        <span className="text-muted">
                                            Samagri
                                        </span>
                                        <span>{formatPaise(samagriDisplay)}</span>
                                    </div>
                                )}
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between fw-bold"
                                    style={{
                                        fontSize: '16px',
                                        color: DARK_RED
                                    }}>
                                    <span>Total</span>
                                    <span>{formatPaise(total)}</span>
                                </div>
                            </div>

                            {/* Payment mode */}
                            <p className="fw-bold mb-2"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                How do you want to pay
                            </p>

                            {[
                                {
                                    value: 'PARTIAL_ADVANCE',
                                    title: 'Token advance',
                                    desc: `Pay ${formatPaise(Math.floor((total * ADVANCE_PERCENTAGE + 50) / 100))} now, rest after pooja`,
                                    badge: 'Recommended'
                                },
                                {
                                    value: 'FULL_ADVANCE',
                                    title: 'Pay full now',
                                    desc: `${formatPaise(total)} upfront`,
                                    badge: null
                                },
                                {
                                    value: 'PAY_AFTER_POOJA',
                                    title: 'Pay after pooja',
                                    desc: 'Nothing now, pay when done',
                                    badge: null
                                }
                            ].map(opt => (
                                <div key={opt.value}
                                    className={`pb-option ${paymentMode === opt.value
                                        ? 'selected' : ''}`}
                                    onClick={() =>
                                        setPaymentMode(opt.value)}>
                                    <div className="pb-radio"></div>
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{
                                                fontSize: '14px',
                                                color: DARK_RED,
                                                fontWeight: 600
                                            }}>
                                                {opt.title}
                                            </span>
                                            {opt.badge && (
                                                <span className="badge"
                                                    style={{
                                                        backgroundColor: GOLD,
                                                        color: DARK_RED,
                                                        fontSize: '10px'
                                                    }}>
                                                    {opt.badge}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mb-0 text-muted"
                                            style={{ fontSize: '12px' }}>
                                            {opt.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>

            {/* ===== STICKY ACTION BAR ===== */}
            <div className="pb-action-bar">
                <div>
                    {step === 1 && service && (
                        <>
                            <p className="mb-0 text-muted"
                                style={{ fontSize: '11px' }}>
                                {date && time
                                    ? `${formatDateShort(date)}, ${formatTime12h(time)}`
                                    : service.poojaType}
                            </p>
                            <p className="mb-0 fw-bold"
                                style={{
                                    fontSize: '17px',
                                    color: DARK_RED
                                }}>
                                {formatPaise(service.priceInPaise)}
                            </p>
                        </>
                    )}
                    {step === 2 && (
                        <p className="mb-0 fw-bold"
                            style={{
                                fontSize: '17px',
                                color: DARK_RED
                            }}>
                            {formatPaise(total)}
                        </p>
                    )}
                    {step === 3 && (
                        <>
                            <p className="mb-0 text-muted"
                                style={{ fontSize: '11px' }}>
                                Pay now {formatPaise(payNow)} · later {formatPaise(payLater)}
                            </p>
                            <p className="mb-0 fw-bold"
                                style={{
                                    fontSize: '17px',
                                    color: DARK_RED
                                }}>
                                {formatPaise(total)}
                            </p>
                        </>
                    )}
                </div>

                <button
                    className="btn fw-bold px-4"
                    style={{
                        backgroundColor: RED,
                        color: GOLD,
                        border: 'none',
                        borderRadius: '10px'
                    }}
                    disabled={saving
                        || (step === 1 && !canGoStep2)
                        || (step === 2 && !canGoStep3)}
                    onClick={step === 3 ? submit : goNext}>
                    {saving
                        ? 'Booking...'
                        : step === 3
                            ? (payNow > 0
                                ? `Confirm & pay ${formatPaise(payNow)}`
                                : 'Confirm booking')
                            : 'Next'}
                </button>
            </div>

            {/* ===== PAYMENT MODAL ===== */}
            {createdBooking && (
                <>
                    <div style={{
                        position: 'fixed', top: 0, left: 0,
                        width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1050
                    }}></div>
                    <div style={{
                        position: 'fixed', top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Complete your payment
                            </div>
                            <div className="card-body p-4">

                                <div className="p-3 mb-3"
                                    style={{
                                        backgroundColor: '#EAF3DE',
                                        borderRadius: '10px'
                                    }}>
                                    <small style={{
                                        color: '#27500A'
                                    }}>
                                        ✓ Booking created. Pay now
                                        to confirm your slot.
                                    </small>
                                </div>

                                <div className="d-flex justify-content-between mb-3">
                                    <span className="text-muted">
                                        Amount to pay
                                    </span>
                                    <strong style={{
                                        fontSize: '20px',
                                        color: RED
                                    }}>
                                        {formatPaise(payNow)}
                                    </strong>
                                </div>

                                {payLater > 0 && (
                                    <p className="text-muted mb-3"
                                        style={{
                                            fontSize: '13px'
                                        }}>
                                        Remaining {formatPaise(payLater)} after
                                        the pooja
                                    </p>
                                )}

                                <label className="form-label fw-bold"
                                    style={{ color: DARK_RED }}>
                                    Payment method
                                </label>
                                <select
                                    className="form-select mb-3"
                                    value={payMethod}
                                    onChange={e =>
                                        setPayMethod(
                                            e.target.value)}
                                    style={{ borderColor: GOLD }}>
                                    <option value="UPI">UPI</option>
                                    <option value="CASH">
                                        Cash
                                    </option>
                                    <option value="CREDIT_CARD">
                                        Credit Card
                                    </option>
                                    <option value="DEBIT_CARD">
                                        Debit Card
                                    </option>
                                    <option value="NET_BANKING">
                                        Net Banking
                                    </option>
                                </select>

                                <button
                                    className="btn w-100 fw-bold mb-2"
                                    style={{
                                        backgroundColor: RED,
                                        color: GOLD,
                                        border: 'none'
                                    }}
                                    onClick={confirmPayment}
                                    disabled={paying}>
                                    {paying
                                        ? 'Processing...'
                                        : `Pay ${formatPaise(payNow)}`}
                                </button>

                                <button
                                    className="btn w-100"
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        color: '#999',
                                        fontSize: '13px'
                                    }}
                                    onClick={skipPayment}
                                    disabled={paying}>
                                    Pay later
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CreateBooking;